import React, { Dispatch, FC, SetStateAction, useContext, useMemo, useState, useCallback, useReducer, useEffect, useRef } from "react";
import BoardTask from "../BoardTask/BoardTask";
import { useNavigate } from "react-router-dom";
import BoardColumnOptions from "../BoardColumnOptions/BoardColumnOptions";
import { AppContext } from "../../../App";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import Tooltip from "../../UiLib/CustomTooltip/CustomTooltip";
import { hasPermission } from "../../../helpers/hasPermisson";
import { BoardContext } from "../../../contexts/BoardContext";
import "./BoardColumn.css"

import { ReactComponent as ProxyToBlue } from "../../../assets/proxyArrow.svg";
import { ReactComponent as ProxyToGray } from "../../../assets/proxyArrowGray.svg";
import { ReactComponent as SettingColumnBlue } from "../../../assets/gorizontThreeButtonBlue.svg";
import { ReactComponent as SettingColumnGray } from "../../../assets/threePointSetings.svg";
import { IColumnDisplay } from "../../../common/api_interfaces/column/IColumnDisplay";
import { IRestrictBoardView } from "../../../common/api_interfaces/board/IRestrictBoardView";

import { ReactComponent as AddDefault } from "../../../assets/images/addDefault.svg";
import Button from "../../UiLib/Button/Button";

interface IBoardColumnProps {
    column: IColumnDisplay;
    userAccess: IFunctionPermissions[];
    columnAccess: boolean;
    proxyBoardAccess: boolean;
    boardsData?: IRestrictBoardView | undefined;
    currentZoom: number;
    previewColumnId: number | undefined;
    setPreviewColumnId: Dispatch<SetStateAction<number | undefined>>;
    previewTaskId: number | undefined;
    setPreviewTaskId: Dispatch<SetStateAction<number | undefined>>;
    onLoadingChange?: (loading: boolean) => void;
}

const BoardColumn: FC<IBoardColumnProps> = ({
    column,
    userAccess,
    columnAccess,
    proxyBoardAccess,
    boardsData,
    currentZoom,
    previewColumnId,
    setPreviewColumnId,
    previewTaskId,
    setPreviewTaskId,
    onLoadingChange
}) => {
    const [dragEventTarget, setDragEventTarget] = useState<EventTarget>();
    const [showOptions, setShowOptions] = useState<boolean>(false);
    const navigate = useNavigate();
    const { showToast } = useContext(AppContext);
    const boardContext = useContext(BoardContext);

    const isProxyColumn = useMemo(() =>
        column.proxyBoardId !== undefined && column.proxyBoardId !== 0,
        [column.proxyBoardId]
    );

    const editColumn = useMemo(() => isProxyColumn ? hasPermission(userAccess, "ColumnProxyAction", "edit") : hasPermission(userAccess, "ColumnAction", "edit"), [userAccess, isProxyColumn]);
    const addTask = useMemo(() => hasPermission(userAccess, "TaskAction", "create"), [userAccess]);
    const viewBoard = useMemo(() => hasPermission(userAccess, "BoardAction", "view"), [userAccess]);


    const dragStateRef = useRef({
        lastColumnId: undefined as number | undefined,
        lastTaskId: undefined as number | undefined,
        throttle: false
    });

    const columnStyle = useMemo(() => {
        const baseStyle = columnAccess ? {} : { opacity: "0.5" };
        return isProxyColumn
            ? { ...baseStyle, minWidth: "400px", maxWidth: "400px" }
            : baseStyle;
    }, [isProxyColumn, columnAccess]);

    const columnHeaderStyle = useMemo(() =>
        isProxyColumn
            ? {
                background: "#009CD40A",
                boxShadow: "0px 0px 6px #149CCE33, 0px 0px 6px #149CCE33",
                border: "1px solid #149CCE33",
                maxWidth: "390px",
                minWidth: "390px",
            }
            : {},
        [isProxyColumn]
    );

    const removePreview = useCallback(() => {
        setPreviewTaskId(undefined);
        setPreviewColumnId(undefined);
    }, [setPreviewTaskId, setPreviewColumnId]);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragEventTarget(e.target);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, columnId: number) => {
        if (!columnAccess) return;

        e.preventDefault();

        // Throttling для производительности
        if (dragStateRef.current.throttle) return;
        dragStateRef.current.throttle = true;

        setTimeout(() => {
            dragStateRef.current.throttle = false;
        }, 50);

        if (
            e.dataTransfer.types.length > 1 ||
            (e.dataTransfer.types.length && e.dataTransfer.types[0] !== "application/json")
        ) return;

        const droppedPosition = e.clientY;
        const column = boardContext.columns.find((c) => c.id === columnId);
        if (!column) return;

        let taskId: number | undefined = undefined;

        // Оптимизированный поиск позиции
        const columnElement = document.getElementById(`tasks-container-${columnId}`);
        if (!columnElement) return;

        const tasks = columnElement.querySelectorAll('.board-task-card');

        for (let i = 0; i < tasks.length; i++) {
            const taskElement = tasks[i] as HTMLElement;
            const rect = taskElement.getBoundingClientRect();
            const midY = rect.top + rect.height / 2;

            if (droppedPosition < midY) {
                taskId = parseInt(taskElement.id.replace('task-', ''));
                break;
            }
        }

        // Предотвращаем лишние обновления состояния
        if (taskId === dragStateRef.current.lastTaskId && columnId === dragStateRef.current.lastColumnId) {
            return;
        }

        dragStateRef.current.lastTaskId = taskId;
        dragStateRef.current.lastColumnId = columnId;

        removePreview();

        if (taskId) {
            setPreviewTaskId(taskId);
        } else if (columnId !== previewColumnId) {
            setPreviewColumnId(columnId);
        }
    }, [columnAccess, boardContext.columns, previewColumnId, removePreview, setPreviewTaskId, setPreviewColumnId]);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        // Проверяем, что мы действительно покидаем колонку, а не переходим на дочерний элемент
        const relatedTarget = e.relatedTarget as Node;
        const currentTarget = e.currentTarget;

        if (!currentTarget.contains(relatedTarget)) {
            removePreview();
            dragStateRef.current.lastColumnId = undefined;
            dragStateRef.current.lastTaskId = undefined;
        }
    }, [removePreview]);

    const handleOnDrop = useCallback((e: React.DragEvent<HTMLDivElement>, columnId: number) => {
        if (!columnAccess) return;

        e.preventDefault();
        try {
            const { taskId } = JSON.parse(e.dataTransfer.getData("application/json"));
            const droppedPosition = e.clientY;

            const newColumn = boardContext.columns.find((c) => c.id === columnId);
            if (!newColumn) return;

            const oldColumn = boardContext.columns.find((c) => c.tasks.find((t) => t.id === taskId) !== undefined);
            if (!oldColumn) return;

            const movedTask = oldColumn.tasks.find((t) => t.id === taskId);
            if (!movedTask) return;

            let insertIndex = newColumn.tasks.length;

            for (let task of newColumn.tasks) {
                const taskElement = document.getElementById(`task-${task.id}`);
                if (taskElement) {
                    const rect = taskElement.getBoundingClientRect();
                    const midY = rect.top + rect.height / 2;
                    if (droppedPosition < midY) {
                        insertIndex = newColumn.tasks.indexOf(task);
                        break;
                    }
                }
            }

            if (oldColumn === newColumn) {
                if (insertIndex !== oldColumn.tasks.indexOf(movedTask)) insertIndex--;
                if (insertIndex < oldColumn.tasks.indexOf(movedTask)) insertIndex++;

                if (insertIndex === oldColumn.tasks.indexOf(movedTask)) return;
            }

            boardContext.handleMoveTask(taskId, columnId, insertIndex);
            removePreview();
        } catch (err) {
            removePreview();
        }
    }, [columnAccess, boardContext.columns, boardContext.handleMoveTask, removePreview]);

    const handleDragEnd = useCallback(() => {
        removePreview();
    }, [removePreview]);

    const chekColumn = useCallback((checked: boolean) => {
        const taskIds = column.tasks.map(t => t.id);
        const taskToColumnIds = column.tasks.map(t => t.taskToColumnId);

        if (!Array.isArray(taskIds) || taskIds.length === 0) {
            showToast("В колонке отсутсвуют задачи! Пожалуйста выберите другую колонку.");
            return;
        }

        boardContext.handleSelectTasks(
            checked
                ? boardContext.selectedTasks.filter(xx => !taskIds.includes(xx))
                : Array.from(new Set([...taskIds, ...boardContext.selectedTasks]))
        );

        boardContext.handleSelectTasksToColumn(
            checked
                ? boardContext.selectedTasksToColumn.filter(xx => !taskToColumnIds.includes(xx))
                : Array.from(new Set([...taskToColumnIds, ...boardContext.selectedTasksToColumn]))
        );
    }, [
        column.tasks,
        boardContext.selectedTasks,
        boardContext.selectedTasksToColumn,
        showToast,
        boardContext.handleSelectTasks,
        boardContext.handleSelectTasksToColumn
    ]);

    const allTasksSelected = useMemo(() =>
        column.tasks.length > 0 && column.tasks.every(task => boardContext.selectedTasks.includes(task.id)),
        [column.tasks, boardContext.selectedTasks]
    );

    const handleProxyClick = useCallback(() => {
        if (proxyBoardAccess) {
            navigate(`/board/${column.proxyBoardId}`);
        } else {
            showToast("У вас нет прав на просмотр доски!");
        }
    }, [columnAccess, column.proxyBoardId, navigate, showToast]);

    const handleAddTaskClick = useCallback(() => {
        if (addTask && columnAccess) {
            boardContext.openAddTaskForm(column.id);
        } else {
            showToast("У вас нет прав на создание задач в данной колонке!");
        }
    }, [addTask, columnAccess, column.id, boardContext.openAddTaskForm, showToast]);

    const handleSettingsClick = useCallback(() => {
        if (editColumn && columnAccess) {
            setShowOptions(true);
        } else {
            showToast("У вас нет прав на редактирование колонки!");
        }
    }, [editColumn, columnAccess, setShowOptions, showToast]);

    const taskList = useMemo(() => {
        const availableColumnsMap = boardContext.columns.filter(c => c.id !== column.id);

        return column.tasks.map((task) => (
            <div key={task.id} className="dash__board--form-column__tasks--list">
                {previewTaskId === task.id && (
                    <div
                        className="task-preview"
                        style={{
                            backgroundColor: "#149cce6b",
                            minHeight: "200px",
                            margin: "6px 0px 6px 0px",
                            borderRadius: "6px",
                            pointerEvents: "none",
                        }}
                    >
                        <div className="create__preview--element__drag--block"></div>
                    </div>
                )}
                <BoardTask
                    task={task}
                    onTaskDragEnd={handleDragEnd}
                    isSelectable={boardContext.actionMode}
                    isSelected={boardContext.selectedTasks.includes(task.id)}
                    userAccess={userAccess}
                    columnAccess={columnAccess}
                    boardsData={boardsData}
                    currentZoom={currentZoom}
                    editTask={hasPermission(userAccess, "TaskAction", "edit")}
                    availableColumns={availableColumnsMap}
                />
            </div>
        ));
    }, [
        column.tasks,
        previewTaskId,
        handleDragEnd,
        boardContext.actionMode,
        boardContext.selectedTasks,
        userAccess,
        columnAccess,
        boardsData,
        currentZoom,
        boardContext.columns,
        boardContext.columns
    ]);

    return (
        <div
            key={column.id}
            className="dash__board--elem droppable-column"
            onDragEnter={handleDragEnter}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDrop={(e) => handleOnDrop(e, column.id)}
            onDragLeave={handleDragLeave}
            style={columnStyle}
        >
            <div
                className="canban-column-default"
                style={columnHeaderStyle}
            >
                <div className="canban-column-header" style={{ background: column.color, border: column.color }}>
                    {boardContext.actionMode && (
                        <input
                            className="files-widget-checkbox"
                            type="checkbox"
                            checked={allTasksSelected}
                            onChange={() => chekColumn(allTasksSelected)}
                        />
                    )}
                    <Tooltip
                        styleContainer={{ width: "500px" }}
                        text={column?.name?.length > 16 ? column.name : ""}
                        placement="bottom"
                        offset={5}
                    >
                        <div className="name__column--style name__column--ellipses">{column?.name}</div>
                    </Tooltip>
                    <div className="canban-column-header__options">
                        <p className="num__task--style">{column?.tasks?.length}</p>
                        {isProxyColumn && (
                            <button onClick={handleProxyClick}>
                                {proxyBoardAccess ? <ProxyToBlue /> : <ProxyToGray />}
                            </button>
                        )}
                        <Button
                            id="button-circle-add"
                            className="board-column-add-task"
                            image={<AddDefault/>}
                            callBack={handleAddTaskClick}
                        />
                        <div>
                            <div
                                className="add__elem--task__button"
                                onClick={handleSettingsClick}
                            >
                                {editColumn && columnAccess ? <SettingColumnBlue /> : <SettingColumnGray />}
                            </div>
                            {showOptions && (
                                <div className="settings__column--popup__edit">
                                    <div
                                        className="full-screen-overlay__board__column__options"
                                        onClick={() => setShowOptions(false)}
                                    ></div>
                                    <BoardColumnOptions column={column} userAccess={userAccess} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div id={`tasks-container-${column.id}`} className="column-task-container">
                    {viewBoard && taskList}
                    {previewColumnId === column.id && (
                        <div
                            className="task-preview"
                            style={{
                                backgroundColor: "#149cce6b",
                                minHeight: "200px",
                                margin: "6px 0px 6px 0px",
                                borderRadius: "6px",
                                pointerEvents: "none",
                            }}
                        >
                            <div className="create__preview--element__drag--block"></div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(BoardColumn);